import { useEffect } from "react";
import { normalizeShortcutKey } from "@aelive/shortcut-service";

function isInputLikeTarget(target) {
  if (!target || !target.tagName) return false;
  const tag = target.tagName.toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || !!target.isContentEditable;
}

// Builds a composite lookup key from a shortcut's modifier flags + base key
// (e.g. "S+F3", "C+F9", or bare "F3"). Used identically for a config entry's
// declared shortcut and for a real KeyboardEvent, so entries that share a
// physical key but differ only by modifier (bare F9 "Log Subs" vs Shift+F9
// "Clock Social Media" vs Ctrl+F9 "Added Time", all defined on the same
// graphic) don't collide the way they did with the previous implementation
// (via @aelive/shortcut-service's subscribeShortcuts), which matched purely
// on base key/keyCode and ignored ctrlKey/altKey/shiftKey entirely — confirmed
// via console: pressing Shift+F9 or Ctrl+F9 both fired the bare-F9 handler.
function buildComboKey({ ctrlKey, altKey, shiftKey, metaKey, key }) {
  if (!key) return null;
  const mods = [ctrlKey && "C", altKey && "A", shiftKey && "S", metaKey && "M"].filter(Boolean).join("");
  return mods ? `${mods}+${key}` : key;
}

// normalizeShortcutKey (from @aelive/shortcut-service) only maps keyCode for
// F1–F12 — any other code (e.g. standard JS keyCodes for letters/digits, 67
// for "C", 68 for "D") silently resolves to "", which drops the shortcut
// entirely (buildComboKey returns null for an empty key). Extend resolution
// locally to cover letters and digits too, still falling back to
// normalizeShortcutKey (and then s.name) for anything else — e.g. F-keys,
// or a keyCode that doesn't resolve to a known key at all.
function resolveBaseKeyFromCode(keyCode) {
  const code = typeof keyCode === "string" ? parseInt(keyCode, 10) : keyCode;
  if (!Number.isFinite(code)) return null;
  if (code >= 65 && code <= 90) return String.fromCharCode(code); // A–Z
  const fKey = normalizeShortcutKey(null, code);
  return fKey || null;
}

export function useCopilotButtonShortcuts(panelConfig, components, enabled) {
  useEffect(() => {
    if (!enabled) return undefined;

    const handlersByCombo = new Map();

    (panelConfig?.tabs || []).forEach((tab) => {
      (tab.actions || []).forEach((action) => {
        const s = action?.shortcut;
        const invoker = components?.[action?.handler?.opusComponent];
        if (!s || typeof invoker !== "function") return;
        // Prefer keyCode for the base key — shortcut.name is sometimes already
        // a compound label (e.g. "C+F9"), while keyCode is always just the
        // plain key; modifiers come from the boolean flags below, never parsed
        // out of the (inconsistently-formatted) name string.
        const baseKey =
          (s.keyCode != null ? resolveBaseKeyFromCode(s.keyCode) : null) ?? normalizeShortcutKey(s.name, null);
        const combo = buildComboKey({
          ctrlKey: s.ctrlKey,
          altKey: s.altKey,
          shiftKey: s.shiftKey,
          metaKey: s.meta ?? s.metaKey,
          key: baseKey,
        });
        if (!combo) return;
        handlersByCombo.set(combo, () => invoker(action.handler?.props || {}));
      });
    });

    if (handlersByCombo.size === 0) return undefined;

    const handleKeyDown = (event) => {
      if (isInputLikeTarget(event.target)) return;
      const combo = buildComboKey({
        ctrlKey: event.ctrlKey,
        altKey: event.altKey,
        shiftKey: event.shiftKey,
        metaKey: event.metaKey,
        key: event.key ? event.key.toUpperCase() : null,
      });
      const handler = combo && handlersByCombo.get(combo);
      if (!handler) return;
      event.preventDefault();
      handler();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [panelConfig, components, enabled]);
}
